import * as THREE from 'three';
import type { Brush } from '../physics/Brush';
import { createArenaMaterials } from './ArenaMaterials';
import type { FacilityArenaBuild } from './FacilityArena';

/**
 * Quiet photo-studio room for first-person viewmodel posing.
 * Player faces a target wall; no enemies, doors, or pickups.
 */
export function buildViewmodelStudio(): FacilityArenaBuild {
  const mats = createArenaMaterials();
  const group = new THREE.Group();
  group.name = 'viewmodel_studio';

  const W = 11;
  const D = 12;
  const H = 3.55;
  const wallT = 0.28;
  const floorY = 0;

  const floorMat = new THREE.MeshStandardMaterial({
    color: 0x2c2926,
    roughness: 0.42,
    metalness: 0.22,
  });
  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x3f3b37,
    roughness: 0.86,
    metalness: 0.04,
  });
  const backWallMat = new THREE.MeshStandardMaterial({
    color: 0x4a4540,
    roughness: 0.9,
    metalness: 0.03,
  });
  const ceilMat = new THREE.MeshStandardMaterial({
    color: 0x1c1a18,
    roughness: 1,
    metalness: 0,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: 0xd4b07a,
    emissive: 0x6a4a20,
    emissiveIntensity: 0.55,
    roughness: 0.35,
    metalness: 0.4,
  });
  const coveMat = new THREE.MeshStandardMaterial({
    color: 0xffe6c4,
    emissive: 0xffd0a0,
    emissiveIntensity: 1.15,
    roughness: 0.4,
    metalness: 0,
  });

  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, D), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = floorY;
  floor.receiveShadow = true;
  group.add(floor);

  const grid = new THREE.GridHelper(W, 22, 0x5a5044, 0x35302c);
  grid.position.y = 0.004;
  const gridMats = Array.isArray(grid.material) ? grid.material : [grid.material];
  for (const m of gridMats) {
    m.transparent = true;
    m.opacity = 0.35;
  }
  group.add(grid);

  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(W, D), ceilMat);
  ceil.rotation.x = Math.PI / 2;
  ceil.position.y = H;
  group.add(ceil);

  const backZ = -D * 0.5;
  const frontZ = D * 0.5;
  const leftX = -W * 0.5;
  const rightX = W * 0.5;

  const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), backWallMat);
  back.position.set(0, H * 0.5, backZ);
  group.add(back);

  const front = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
  front.rotation.y = Math.PI;
  front.position.set(0, H * 0.5, frontZ);
  group.add(front);

  const left = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wallMat);
  left.rotation.y = Math.PI / 2;
  left.position.set(leftX, H * 0.5, 0);
  group.add(left);

  const right = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wallMat);
  right.rotation.y = -Math.PI / 2;
  right.position.set(rightX, H * 0.5, 0);
  group.add(right);

  const skirting = new THREE.Mesh(new THREE.BoxGeometry(W - 0.04, 0.08, 0.04), trimMat);
  skirting.position.set(0, 0.04, backZ + 0.02);
  group.add(skirting);

  const cove = new THREE.Mesh(new THREE.BoxGeometry(W - 0.6, 0.04, 0.08), coveMat);
  cove.position.set(0, H - 0.12, backZ + 0.06);
  group.add(cove);
  const coveL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.04, D - 0.6), coveMat);
  coveL.position.set(leftX + 0.08, H - 0.12, 0);
  group.add(coveL);
  const coveR = coveL.clone();
  coveR.position.x = rightX - 0.08;
  group.add(coveR);

  const eyeY = 1.68;
  group.add(makeAimTarget(0, eyeY, backZ + 0.02));

  const horizon = new THREE.Mesh(
    new THREE.PlaneGeometry(W * 0.92, 0.012),
    new THREE.MeshBasicMaterial({ color: 0xc8b090, transparent: true, opacity: 0.28 }),
  );
  horizon.position.set(0, eyeY, backZ + 0.015);
  group.add(horizon);

  const hemi = new THREE.HemisphereLight(0xe8dcc8, 0x2a241c, 0.55);
  group.add(hemi);

  const key = new THREE.DirectionalLight(0xfff2e0, 1.05);
  key.position.set(2.4, 4.2, 3.5);
  key.castShadow = false;
  group.add(key);

  const fill = new THREE.DirectionalLight(0xb0c4dc, 0.28);
  fill.position.set(-3.2, 2.2, 1.5);
  group.add(fill);

  const rim = new THREE.PointLight(0xffc090, 4.5, 14, 1.6);
  rim.position.set(0, H - 0.4, backZ + 1.2);
  group.add(rim);

  const lamps: THREE.Light[] = [hemi, key, fill, rim];

  const brushes: Brush[] = [
    {
      id: 'floor_studio',
      kind: 'box',
      center: new THREE.Vector3(0, -0.15, 0),
      size: new THREE.Vector3(W + 1, 0.3, D + 1),
      yaw: 0,
    },
    {
      id: 'wall_back',
      kind: 'box',
      center: new THREE.Vector3(0, H * 0.5, backZ - wallT * 0.5),
      size: new THREE.Vector3(W + wallT, H, wallT),
      yaw: 0,
    },
    {
      id: 'wall_front',
      kind: 'box',
      center: new THREE.Vector3(0, H * 0.5, frontZ + wallT * 0.5),
      size: new THREE.Vector3(W + wallT, H, wallT),
      yaw: 0,
    },
    {
      id: 'wall_left',
      kind: 'box',
      center: new THREE.Vector3(leftX - wallT * 0.5, H * 0.5, 0),
      size: new THREE.Vector3(wallT, H, D),
      yaw: 0,
    },
    {
      id: 'wall_right',
      kind: 'box',
      center: new THREE.Vector3(rightX + wallT * 0.5, H * 0.5, 0),
      size: new THREE.Vector3(wallT, H, D),
      yaw: 0,
    },
  ];

  return {
    group,
    brushes,
    lights: lamps,
    spawn: new THREE.Vector3(0, 0, 2.6),
    spawnYaw: 0,
    doors: [],
    mats,
    enemySpawns: [],
    pickups: [],
    rooms: [],
    bakeLights: [],
  };
}

function makeAimTarget(x: number, y: number, z: number): THREE.Group {
  const root = new THREE.Group();
  root.position.set(x, y, z);

  const rings: Array<[number, number, number]> = [
    [0.62, 0.5, 0x6a5848],
    [0.5, 0.38, 0xd8b47a],
    [0.38, 0.26, 0x6a5848],
    [0.26, 0.14, 0xd8b47a],
    [0.14, 0.045, 0x6a5848],
  ];
  for (const [outer, inner, color] of rings) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(inner, outer, 64),
      new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }),
    );
    root.add(ring);
  }
  const dot = new THREE.Mesh(
    new THREE.CircleGeometry(0.028, 24),
    new THREE.MeshBasicMaterial({ color: 0xf4e6c8, side: THREE.DoubleSide }),
  );
  root.add(dot);

  const plusMat = new THREE.MeshBasicMaterial({
    color: 0xf7edd8,
    transparent: true,
    opacity: 0.85,
    side: THREE.DoubleSide,
  });
  const barH = new THREE.Mesh(new THREE.PlaneGeometry(0.72, 0.01), plusMat);
  const barV = new THREE.Mesh(new THREE.PlaneGeometry(0.01, 0.72), plusMat);
  barH.position.z = 0.002;
  barV.position.z = 0.002;
  root.add(barH, barV);
  return root;
}
